import { useState, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { listNotifications, markAllNotificationsRead } from '../api/notifications';
import { Notification } from '../types/notification';
import { PRIORITY_COLOR, getNotificationMeta } from '../config/notificationMeta';
import { useOpenNotification } from '../hooks/useOpenNotification';
import { useNotificationSummary } from '../hooks/useNotificationSummary';
import { getPopupsEnabled, getSoundEnabled, setPopupsEnabled, setSoundEnabled } from '../config/notificationPreferences';

const timeAgo = (iso: string) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

export const NotificationBell = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLElement | null>(null);
  const [popupsEnabled, setPopupsEnabledState] = useState(getPopupsEnabled);
  const [soundEnabled, setSoundEnabledState] = useState(getSoundEnabled);
  const open = !!anchorEl;

  // The unread count comes from the shared notification summary (one 60s
  // poll for the whole app, 2026-11-03) rather than its own 15s poll.
  const summaryQuery = useNotificationSummary();

  const notificationsQuery = useQuery({
    queryKey: ['notifications-recent'],
    queryFn: () => listNotifications({ page: 1, pageSize: 10 }),
    enabled: open,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['notifications-recent'] });
    queryClient.invalidateQueries({ queryKey: ['notification-summary'] });
  };

  const openNotification = useOpenNotification();
  const markAllReadMutation = useMutation({ mutationFn: markAllNotificationsRead, onSuccess: invalidate });

  const handleOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  // §20: opening the dropdown must NOT mark everything read — only an
  // explicit click on a notification, or "Mark all read", does.
  const handleNotificationClick = (notification: Notification) => {
    handleClose();
    void openNotification(notification);
  };

  const unreadCount = summaryQuery.data?.unreadNotifications ?? 0;
  const notifications = notificationsQuery.data?.data ?? [];

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen} aria-label="notifications" sx={{ ml: 1 }}>
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { sx: { width: 400, maxHeight: 520 } } }}
      >
        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Notifications
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {unreadCount > 0 && (
              <Button size="small" onClick={() => markAllReadMutation.mutate()}>
                Mark all read
              </Button>
            )}
            <Tooltip title="Notification settings">
              <IconButton size="small" onClick={(e) => setSettingsAnchorEl(e.currentTarget)}>
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
        <Divider />
        {notifications.length === 0 ? (
          <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
            {notificationsQuery.isLoading ? (
              <Typography variant="body2" color="text.secondary">
                Loading…
              </Typography>
            ) : notificationsQuery.isError ? (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Couldn&rsquo;t load notifications
                </Typography>
                <Button size="small" onClick={() => notificationsQuery.refetch()} sx={{ mt: 0.5 }}>
                  Retry
                </Button>
              </>
            ) : (
              <>
                <CheckCircleOutlineIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  You&rsquo;re all caught up!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No new notifications right now.
                </Typography>
              </>
            )}
          </Box>
        ) : (
          <MenuList dense disablePadding>
            {notifications.map((notification) => {
              const meta = getNotificationMeta(notification.type);
              const isUnread = !notification.readAt;
              return (
                <MenuItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    alignItems: 'flex-start',
                    gap: 1.25,
                    whiteSpace: 'normal',
                    bgcolor: isUnread ? 'action.hover' : 'transparent',
                    borderLeft: '3px solid',
                    borderLeftColor: isUnread ? PRIORITY_COLOR[meta.priority] : 'transparent',
                  }}
                >
                  <Box sx={{ color: PRIORITY_COLOR[meta.priority], display: 'flex', pt: 0.5 }}>
                    <meta.icon fontSize="small" />
                  </Box>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: isUnread ? 700 : 500 }}>
                        {notification.title}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="text.secondary">
                          {notification.message}
                        </Typography>
                        <br />
                        <Typography component="span" variant="caption" color="text.secondary">
                          {timeAgo(notification.createdAt)}
                        </Typography>
                      </>
                    }
                  />
                </MenuItem>
              );
            })}
          </MenuList>
        )}
        <Divider />
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <Button
            size="small"
            onClick={() => {
              handleClose();
              navigate('/notifications');
            }}
          >
            View all notifications →
          </Button>
        </Box>
      </Menu>

      <Menu anchorEl={settingsAnchorEl} open={!!settingsAnchorEl} onClose={() => setSettingsAnchorEl(null)}>
        <Box sx={{ px: 2, py: 1, width: 260 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Notification settings
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="body2">Desktop popups</Typography>
            <Switch
              size="small"
              checked={popupsEnabled}
              onChange={(e) => {
                setPopupsEnabled(e.target.checked);
                setPopupsEnabledState(e.target.checked);
              }}
            />
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2">Notification sound</Typography>
            <Switch
              size="small"
              checked={soundEnabled}
              onChange={(e) => {
                setSoundEnabled(e.target.checked);
                setSoundEnabledState(e.target.checked);
              }}
            />
          </Stack>
        </Box>
      </Menu>
    </>
  );
};
